import styled from '@emotion/styled';
import { Button } from 'react-bootstrap';
import React, { useCallback } from 'react';

import { palette } from '~/utils/colours';
import Modal, { ModalProps } from '~/App/Components/Modal';

const ModalMessage = styled.div`
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.48px;
  text-align: center;
  color: ${palette.brownishGrey};
  margin-bottom: 20px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  button {
    margin: 0 10px;
  }
`;

const Heading = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 26px;
  color: #666666;
`;

const Message = styled.div`
  max-width: 65%;
  margin: 0 auto;
`;

interface TitleProps {
  text: string | null;
}

const Title = ({ text }: TitleProps) => {
  if (!text) {
    return <></>;
  }

  return <Heading>{text}</Heading>;
};

export interface ConfirmationModalProps
  extends Pick<ModalProps, 'isOpen' | 'isLoading'> {
  setOpen: (val: boolean) => void;
  onConfirm?: (save: boolean) => Promise<void>;
  title?: string | null;
  message?: string;
}

export default function ConfirmationModal({
  isOpen,
  setOpen,
  isLoading,
  onConfirm,
  title = 'Save this template',
  message = 'You have unsaved changes. Do you want to save changes before exiting the editor?',
}: ConfirmationModalProps) {
  const closeModal = useCallback(() => setOpen(false), [setOpen]);

  const saveAndClose = async (shouldSave: boolean) => {
    await onConfirm?.(shouldSave);
    closeModal();
  };

  const modalProps: ModalProps = {
    isOpen: isOpen,
    onRequestClose: closeModal,
    isLoading,
    hideActions: true,
  };

  return (
    <Modal {...modalProps}>
      <ModalMessage>
        <Title text={title} />
        <Message>{message}</Message>
      </ModalMessage>
      <Actions>
        <Button bsStyle='info' onClick={closeModal}>
          Cancel
        </Button>
        <Button bsStyle='danger' onClick={() => saveAndClose(false)}>
          {"No, don't save"}
        </Button>
        <Button bsStyle='primary' onClick={() => saveAndClose(true)}>
          Yes, save
        </Button>
      </Actions>
    </Modal>
  );
}
